import React, { useState } from 'react';
import Button from '../UI/Button';
import { cvService } from '../../services/cvService';
import { CVProfile, PDFExportResult } from './types';
import { createStandardError, getUserFriendlyMessage, logError } from './utils/errorHandling';
import { useRetry } from './hooks/useRetry';
import CVAnalyzeSection from "./CVAnalyzeButton";

interface CVExportSectionProps {
  cvData: CVProfile | null;
  onExport?: (pdfUrl: string) => void;
}

interface ExportState {
  isExporting: boolean;
  isDownloading: boolean;
  progress: number;
  error: string | null;
  lastExportResult: PDFExportResult | null;
}

/**
 * CV Export Section component
 * Handles PDF export functionality with progress indicator and error handling
 */
const CVExportSection: React.FC<CVExportSectionProps> = ({ cvData, onExport }) => {
  const [exportState, setExportState] = useState<ExportState>({
    isExporting: false,
    isDownloading: false,
    progress: 0,
    error: null,
    lastExportResult: null
  });

  const { executeWithRetry } = useRetry();

  /**
   * Updates export progress for user feedback
   */
  const updateProgress = (progress: number) => {
    setExportState(prev => ({ ...prev, progress }));
  };

  /**
   * Handles PDF export operation with progress tracking
   */
  const handleExportToPDF = async () => {
    if (!cvData?.id) {
      setExportState(prev => ({
        ...prev,
        error: 'Data CV tidak tersedia untuk diekspor. Silakan simpan CV Anda terlebih dahulu.'
      }));
      return;
    }

    setExportState(prev => ({
      ...prev,
      isExporting: true,
      error: null,
      progress: 0
    }));

    try {
      // Start progress indication
      updateProgress(10);

      // Call export API with retry logic
      const result = await executeWithRetry(() => cvService.exportToPDF(cvData.id));

      updateProgress(80);

      if (result.success && result.downloadUrl) {
        updateProgress(100);
        
        setExportState(prev => ({
          ...prev,
          isExporting: false,
          lastExportResult: result,
          progress: 100
        }));

        // Call onExport callback if provided
        if (onExport) {
          onExport(result.downloadUrl);
        }

        // Auto-download after a brief delay to show completion
        setTimeout(() => {
          handleDownloadPDF(result.downloadUrl!, result.filename);
        }, 500);

      } else {
        throw new Error(result.error || 'PDF export failed');
      }

    } catch (error) {
      const standardError = createStandardError(error, 'Failed to export CV to PDF');
      logError(standardError, 'CVExportSection.handleExportToPDF', { cvId: cvData.id });

      setExportState(prev => ({
        ...prev,
        isExporting: false,
        error: getUserFriendlyMessage(standardError),
        progress: 0
      }));
    }
  };

  /**
   * Handles PDF download with proper file handling
   */
  const handleDownloadPDF = async (downloadUrl?: string, filename?: string) => {
    if (!cvData?.id) {
      return;
    }

    const urlToUse = downloadUrl || exportState.lastExportResult?.downloadUrl;
    const filenameToUse = filename || exportState.lastExportResult?.filename || `CV_${cvData.full_name}_${new Date().toISOString().split('T')[0]}.pdf`;

    if (!urlToUse) {
      setExportState(prev => ({
        ...prev,
        error: 'URL unduhan tidak tersedia. Silakan ekspor CV terlebih dahulu.'
      }));
      return;
    }

    setExportState(prev => ({
      ...prev,
      isDownloading: true,
      error: null
    }));

    try {
      // If it's a direct URL, use it directly
      if (urlToUse.startsWith('http')) {
        const link = document.createElement('a');
        link.href = urlToUse;
        link.download = filenameToUse;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        // Use the service method for blob download
        const blob = await executeWithRetry(() => cvService.downloadPDF(cvData.id, filenameToUse));

        // Create blob URL and download
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = filenameToUse;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // Clean up blob URL
        window.URL.revokeObjectURL(blobUrl);
      }

      setExportState(prev => ({
        ...prev,
        isDownloading: false
      }));

    } catch (error) {
      const standardError = createStandardError(error, 'Failed to download PDF');
      logError(standardError, 'CVExportSection.handleDownloadPDF', { 
        cvId: cvData.id, 
        downloadUrl: urlToUse,
        filename: filenameToUse 
      });

      setExportState(prev => ({
        ...prev,
        isDownloading: false,
        error: getUserFriendlyMessage(standardError)
      }));
    }
  };

  /**
   * Clears error state
   */
  const clearError = () => {
    setExportState(prev => ({ ...prev, error: null }));
  };

  /**
   * Retry export operation
   */
  const retryExport = () => {
    clearError();
    handleExportToPDF();
  };

  const { isExporting, isDownloading, progress, error, lastExportResult } = exportState;
  const isLoading = isExporting || isDownloading;
  const canExport = cvData?.id && !isLoading;
  const hasExportResult = lastExportResult?.success && lastExportResult.downloadUrl;

  return (
    <div className="cv-export-section">
      <div className="flex flex-wrap">
        <div className="w-full">
          {/* Export Description */}

          {/* Export Status */}
          {!cvData?.id && (
            <div className="p-4 rounded border bg-yellow-50 border-yellow-200 text-yellow-800 mb-4">
              <i className="fas fa-exclamation-triangle mr-2"></i>
              Silakan simpan informasi CV Anda sebelum mengekspor ke PDF.
            </div>
          )}

          {/* Progress Indicator */}
          {isLoading && (
            <div className="mb-4">
              <div className="flex items-center mb-2">
                <div
                  className="animate-spin rounded-full border-4 border-gray-300 border-t-blue-600 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600 w-4 h-4 mr-2"
                  role="status"
                >
                  <span className="visually-hidden">Memuat...</span>
                </div>
                <span className="text-gray-500">
                  {isExporting ? "Membuat PDF..." : "Mengunduh..."}
                </span>
              </div>

              {isExporting && progress > 0 && (
                <div className="progress" style={{ height: "8px" }}>
                  <div
                    className="progress-bar progress-bar-striped progress-bar-animated"
                    role="progressbar"
                    style={{ width: `${progress}%` }}
                    aria-valuenow={progress}
                    aria-valuemin={0}
                    aria-valuemax={100}
                  ></div>
                </div>
              )}
            </div>
          )}

          {/* Error Display */}
          {error && (
            <div className="p-4 rounded border bg-red-50 border-red-200 text-red-800 mb-4 flex items-center justify-between">
              <div>
                <i className="fas fa-exclamation-circle mr-2"></i>
                {error}
              </div>
              <div>
                <Button
                  onClick={retryExport}
                  className="mr-2"
                  size="sm"
                  secondary
                  disabled={isLoading}
                >
                  <i className="fas fa-redo mr-1"></i>
                  Coba Lagi
                </Button>
                <Button onClick={clearError} size="sm" outlinePrimary>
                  <i className="fas fa-times"></i>
                </Button>
              </div>
            </div>
          )}

          {/* Success Message */}
          {hasExportResult && !error && !isLoading && (
            <div className="p-4 rounded border bg-green-50 border-green-200 text-green-800 mb-4">
              <i className="fas fa-check-circle mr-2"></i>
              PDF berhasil dibuat!
              {lastExportResult.filename && (
                <span className="ml-1">({lastExportResult.filename})</span>
              )}
            </div>
          )}

          {/* Export Actions */}
          <div className="flex flex-col gap-3">
            <Button
              onClick={handleExportToPDF}
              primary
              disabled={!canExport}
              block
              className="flex items-center justify-center"
            >
              {isExporting ? (
                <>
                  <div
                    className="animate-spin rounded-full border-4 border-gray-300 border-t-blue-600 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600 w-4 h-4 mr-2"
                    role="status"
                  >
                    <span className="visually-hidden">Memuat...</span>
                  </div>
                  Membuat PDF...
                </>
              ) : (
                <>
                  <i className="fas fa-file-pdf mr-2"></i>
                  Buat PDF
                </>
              )}
            </Button>

            {hasExportResult && (
              <Button
                onClick={() => handleDownloadPDF()}
                secondary
                disabled={isDownloading}
                className="flex items-center w-full"
              >
                {isDownloading ? (
                  <>
                    <div
                      className="animate-spin rounded-full border-4 border-gray-300 border-t-blue-600 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600 w-4 h-4 mr-2"
                      role="status"
                    >
                      <span className="visually-hidden">Memuat...</span>
                    </div>
                    Mengunduh...
                  </>
                ) : (
                  <>
                    <i className="fas fa-download mr-2"></i>
                    Unduh Lagi
                  </>
                )}
              </Button>
            )}
            <CVAnalyzeSection cvData={cvData} />
          </div>

          {/* Export Information */}
          <div className="mt-4">
            <small className="text-gray-500">
              <i className="fas fa-info-circle mr-1"></i>
              PDF akan mencakup semua bagian CV yang telah diisi dalam format
              profesional.
            </small>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CVExportSection;